import config from '../../config';
import { supabase } from '../authService/supabase';

export type PublishedAppVisibility = 'PUBLIC' | 'USER_ALLOWLIST';

export interface PublishedAppInvitation {
  id: string;
  email: string;
  status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  expiresAt: string;
}

export interface PublishedAppSharing {
  publicationId: string;
  displayName: string;
  hostname: string;
  publicUrl: string;
  visibility: PublishedAppVisibility;
  policyVersion: number;
  invitations: PublishedAppInvitation[];
}

export interface PublishedAppShareIntent {
  intent: string;
  publicationId: string;
  displayName: string;
  hostname: string;
  expiresInSeconds: number;
}

export interface PublishedAppAccessTicket {
  publicationId: string;
  hostname: string;
  accessUrl: string;
  publicAccess: boolean;
  expiresInSeconds: number;
}

interface ApiEnvelope<T> {
  code?: string;
  message?: string;
  status?: string;
  data?: T;
}

export class PublishedAppApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus: number,
  ) {
    super(message);
    this.name = 'PublishedAppApiError';
  }
}

const apiUrl = (path: string): string => {
  const origin = String(
    import.meta.env.VITE_PUBLISHED_APPS_ACCOUNT_API_HOST
      || config.services.auth.apiHost
      || '',
  ).replace(/\/+$/, '');
  return `${origin}${path}`;
};

const request = async <T>(
  path: string,
  options: RequestInit & { authenticated?: boolean; allowEmpty?: boolean } = {},
): Promise<T> => {
  const { authenticated = false, allowEmpty = false, headers, ...requestOptions } = options;
  let accessToken = '';

  if (authenticated) {
    const sessionResult = await supabase.auth.getSession();
    accessToken = sessionResult.data.session?.access_token || '';
    if (!accessToken) {
      throw new PublishedAppApiError('LOGIN_REQUIRED', 'Please sign in to continue', 401);
    }
  }

  const response = await fetch(apiUrl(path), {
    ...requestOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
  });
  const payload = await response.json().catch(() => null) as ApiEnvelope<T> | null;
  if (!response.ok || payload?.status === '1' || (!allowEmpty && payload?.data === undefined)) {
    throw new PublishedAppApiError(
      payload?.code || `HTTP_${response.status}`,
      payload?.message || 'Unable to complete this request.',
      response.status,
    );
  }
  return payload?.data as T;
};

export const publishedAppSharingService = {
  getSharing(publicationId: string) {
    return request<PublishedAppSharing>(
      `/api/v1/published-apps/${encodeURIComponent(publicationId)}/sharing`,
      { authenticated: true },
    );
  },

  updateVisibility(publicationId: string, visibility: PublishedAppVisibility) {
    return request<PublishedAppSharing>(
      `/api/v1/published-apps/${encodeURIComponent(publicationId)}/visibility`,
      { method: 'PUT', body: JSON.stringify({ visibility }), authenticated: true },
    );
  },

  invite(publicationId: string, email: string) {
    return request<PublishedAppInvitation>(
      `/api/v1/published-apps/${encodeURIComponent(publicationId)}/invitations`,
      { method: 'POST', body: JSON.stringify({ email }), authenticated: true },
    );
  },

  revokeInvitation(publicationId: string, invitationId: string) {
    return request<unknown>(
      `/api/v1/published-apps/${encodeURIComponent(publicationId)}/invitations/${encodeURIComponent(invitationId)}`,
      { method: 'DELETE', authenticated: true, allowEmpty: true },
    );
  },

  createIntent(token: string) {
    return request<PublishedAppShareIntent>('/api/v1/published-app-share/intents', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  issueTicket(input: { intent?: string; hostname?: string }) {
    return request<PublishedAppAccessTicket>('/api/v1/published-app-share/ticket', {
      method: 'POST',
      body: JSON.stringify(input),
      authenticated: true,
    });
  },
};
